import React, { Component, ReactNode } from 'react';

import { errorHandling, announceToScreenReader } from '../../utils';

import { Button } from './Button';
import { Card, CardHeader, CardContent } from './Card';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log the error securely
    errorHandling.logSecurityError('React Error Boundary caught an error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Announce error to screen readers
    announceToScreenReader(
      'An error occurred. Please try refreshing the page or contact support if the problem persists.',
      'assertive'
    );

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    announceToScreenReader('Page refreshed. Please try your action again.');
  };

  handleReload = () => {
    window.location.reload();
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const sanitizedErrorMessage = this.state.error
        ? errorHandling.sanitizeErrorMessage(this.state.error)
        : 'An unexpected error occurred';

      return (
        <div
          className="min-h-screen bg-gray-50 flex items-center justify-center p-4"
          role="main"
          aria-labelledby="error-title"
        >
          <Card className="max-w-md w-full">
            <CardHeader>
              <div className="flex items-center space-x-3">
                <div
                  className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center"
                  aria-hidden="true"
                >
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <div>
                  <h1
                    id="error-title"
                    className="text-lg font-semibold text-gray-900"
                  >
                    Something went wrong
                  </h1>
                  <p className="text-sm text-gray-600">
                    We apologize for the inconvenience
                  </p>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="text-sm text-gray-700">
                <p>{sanitizedErrorMessage}</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={this.handleRetry}
                  variant="primary"
                  className="flex-1"
                  aria-describedby="retry-description"
                >
                  Try Again
                </Button>
                <Button
                  onClick={this.handleReload}
                  variant="outline"
                  className="flex-1"
                  aria-describedby="reload-description"
                >
                  Reload Page
                </Button>
              </div>

              <div className="text-xs text-gray-500 space-y-1">
                <p id="retry-description">
                  Try Again: Attempts to recover from the error without
                  reloading the page
                </p>
                <p id="reload-description">
                  Reload Page: Refreshes the entire page to start fresh
                </p>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4">
                  <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                    Developer Information
                  </summary>
                  <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono text-gray-800 overflow-auto max-h-40">
                    <div className="mb-2">
                      <strong>Error:</strong> {this.state.error.message}
                    </div>
                    {this.state.error.stack && (
                      <div className="mb-2">
                        <strong>Stack:</strong>
                        <pre className="whitespace-pre-wrap mt-1">
                          {this.state.error.stack}
                        </pre>
                      </div>
                    )}
                    {this.state.errorInfo?.componentStack && (
                      <div>
                        <strong>Component Stack:</strong>
                        <pre className="whitespace-pre-wrap mt-1">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook version for functional components
export const useErrorHandler = () => {
  const handleError = React.useCallback(
    (error: Error, errorInfo?: React.ErrorInfo) => {
    errorHandling.logSecurityError('Application error', {
      error: error.message,
      stack: error.stack,
      ...errorInfo,
    });

    announceToScreenReader(
      'An error occurred. Please check the form and try again.',
      'assertive'
    );
    },
    []
  );

  return handleError;
};
